<!-- Fichier: frontend/src/views/MeetingDetailView.vue -->
<!-- Version 6 - Formulaire d'Édition Simplifié -->

<template>
  <div class="meeting-detail-page" v-if="meeting">
    <!-- Bloc 1: Entête de la Réunion -->
    <div class="app-block meeting-header-block">
      <div class="app-block-title">
        <FeatherIcon type="clipboard" size="24"></FeatherIcon>
        <h1 style="margin-left: 25px">{{ meeting.title }}</h1>
        <!-- Boutons d'action pour la réunion -->
        <div class="meeting-actions">
          <BaseButton
            variant="secondary"
            size="sm"
            @click="editMeeting(meeting.id)"
            :disabled="loading || actionLoading || editingActionId !== null"
          >
            <FeatherIcon type="edit-3" size="16"></FeatherIcon> Modifier
          </BaseButton>
          <BaseButton
            variant="danger"
            size="sm"
            @click="deleteMeeting(meeting.id)"
            :disabled="loading || actionLoading || editingActionId !== null"
          >
            <FeatherIcon
              v-if="loading || actionLoading"
              type="loader"
              size="16"
              animation="spin"
            ></FeatherIcon>
            <FeatherIcon v-else type="trash-2" size="16"></FeatherIcon>
            Supprimer
          </BaseButton>
        </div>
      </div>
      <div class="meeting-meta">
        <p>
          <FeatherIcon type="calendar" size="14"></FeatherIcon>
          {{ formattedDate }}
          <span v-if="meeting.meeting_time"> à {{ formattedTime }}</span>
        </p>
        <!-- Future: Participants -->
      </div>
    </div>

    <!-- Bloc 2: Bloc des Notes -->
    <div class="app-block notes-block">
      <div class="app-block-title">
        <FeatherIcon type="book-open" size="24"></FeatherIcon>
        <h2 style="margin-left: 25px">Notes de Réunion</h2>
        <!-- Future: Edit Notes Button (si pas édition inline) -->
      </div>
      <div class="notes-content">
        <p>{{ meeting.notes || "Aucune note pour cette réunion." }}</p>
        <!-- TODO: Implement richer note editing later -->
      </div>
    </div>

    <!-- Bloc 3: Bloc des Actions -->
    <div class="app-block actions-block">
      <div class="app-block-title">
        <FeatherIcon type="check-square" size="24"></FeatherIcon>
        <h2 style="margin-left: 25px">Actions à Suivre</h2>
      </div>

      <!-- Formulaire d'ajout d'action inline - Caché si une action est en cours d'édition -->
      <form
        @submit.prevent="createAction"
        class="add-action-form"
        v-if="!editingActionId"
      >
        <input
          type="text"
          v-model="newAction.description"
          placeholder="Nouvelle action..."
          required
          :disabled="actionLoading"
        />
        <input
          type="date"
          v-model="newAction.due_date"
          placeholder="Échéance"
          :disabled="actionLoading"
        />
        <input
          type="text"
          v-model="newAction.assigned_to"
          placeholder="Assigné à"
          :disabled="actionLoading"
        />
        <BaseButton
          type="submit"
          variant="accent"
          :loading="actionLoading"
          :disabled="actionLoading || !newAction.description"
        >
          <FeatherIcon
            v-if="actionLoading"
            type="loader"
            size="16"
            animation="spin"
          ></FeatherIcon>
          <FeatherIcon v-else type="plus" size="16"></FeatherIcon>
          Ajouter
        </BaseButton>
      </form>
      <p v-if="actionError && !editingActionId" class="error-message">
        {{ actionError }}
      </p>

      <div class="actions-list">
        <div v-if="meeting.actions && meeting.actions.length > 0">
          <!-- Utilisation de TransitionGroup pour les animations d'ajout/suppression -->
          <TransitionGroup name="list" tag="div">
            <div
              v-for="action in meeting.actions"
              :key="action.id"
              class="action-item"
              :class="{
                'is-editing': editingActionId === action.id,
                'is-loading': actionLoading && editingActionId === action.id,
              }"
            >
              <!-- CONTENU DE L'ACTION : NORMAL ou EDITION -->

              <!-- Affichage NORMAL (caché en mode édition) -->
              <template v-if="editingActionId !== action.id">
                <!-- Icône de statut cliquable -->
                <div
                  class="action-status-icon"
                  @click="toggleActionStatus(action)"
                  :class="`status-${action.status}`"
                >
                  <FeatherIcon
                    :type="getActionStatusIcon(action.status)"
                    size="18"
                  ></FeatherIcon>
                </div>

                <span
                  class="action-description"
                  :class="`status-${action.status}`"
                  style="margin-left: 25px"
                  >{{ action.description }}</span
                >

                <div class="action-meta" :class="`status-${action.status}`">
                  <span v-if="action.assigned_to"
                    ><FeatherIcon type="user" size="12"></FeatherIcon>
                    {{ action.assigned_to }}</span
                  >
                  <span v-if="action.due_date"
                    ><FeatherIcon type="calendar" size="12"></FeatherIcon>
                    {{ formattedActionDate(action.due_date) }}</span
                  >
                </div>

                <!-- Boutons d'action (modifier/supprimer) -->
                <div class="action-item-actions">
                  <FeatherIcon
                    type="edit-3"
                    size="16"
                    class="action-icon action-icon--edit"
                    @click="startEditAction(action)"
                  />
                  <FeatherIcon
                    type="trash-2"
                    size="16"
                    class="action-icon action-icon--delete"
                    @click="deleteAction(action.id)"
                  />
                </div>
              </template>

              <!-- Formulaire d'édition inline (visible seulement en mode édition) -->
              <template
                v-else-if="
                  currentEditingAction && editingActionId === action.id
                "
              >
                <!-- Icône de statut visible pendant l'édition -->
                <div
                  class="action-status-icon"
                  :class="`status-${currentEditingAction.status}`"
                >
                  <FeatherIcon
                    :type="getActionStatusIcon(currentEditingAction.status)"
                    size="18"
                  ></FeatherIcon>
                </div>
                <!-- NOUVEAU : Formulaire pour l'édition inline -->
                <form @submit.prevent="saveEditAction" class="action-edit-form">
                  <input
                    type="text"
                    v-model="currentEditingAction.description"
                    class="edit-input"
                    required
                    :disabled="actionLoading"
                    placeholder="Description"
                  />
                  <input
                    type="date"
                    v-model="currentEditingAction.due_date"
                    class="edit-input"
                    :disabled="actionLoading"
                    placeholder="Échéance"
                  />
                  <input
                    type="text"
                    v-model="currentEditingAction.assigned_to"
                    class="edit-input"
                    :disabled="actionLoading"
                    placeholder="Assigné à"
                  />
                  <div class="action-item-actions edit-mode">
                    <!-- Bouton de validation -->
                    <button
                      type="submit"
                      class="button-submit-edit"
                      :disabled="
                        actionLoading || !currentEditingAction.description
                      "
                    >
                      <FeatherIcon
                        v-if="actionLoading"
                        type="loader"
                        size="16"
                        animation="spin"
                      ></FeatherIcon>
                      <FeatherIcon v-else type="check" size="16"></FeatherIcon>
                    </button>
                    <!-- Bouton Annuler -->
                    <button
                      type="button"
                      class="button-cancel-edit"
                      @click.stop="cancelEditAction"
                      :disabled="actionLoading"
                    >
                      <FeatherIcon type="x" size="16"></FeatherIcon>
                    </button>
                  </div>
                </form>
              </template>
            </div>
          </TransitionGroup>
        </div>
        <div v-else class="no-actions-message">
          Aucune action définie pour cette réunion.
        </div>
        <p v-if="actionError && editingActionId !== null" class="error-message">
          {{ actionError }}
        </p>
      </div>
    </div>

    <!-- Bouton de retour -->
    <div class="back-button-block">
      <BaseButton
        variant="secondary"
        @click="$router.push('/')"
        :disabled="loading || actionLoading || editingActionId !== null"
      >
        <FeatherIcon type="arrow-left" size="16"></FeatherIcon> Retour à la
        liste
      </BaseButton>
    </div>
  </div>
  <!-- Bloc affiché si la réunion n'est PAS trouvée ou en chargement/erreur -->
  <div v-else>
    <div class="app-block message-block">
      <p v-if="loading">Chargement de la réunion...</p>
      <p v-else-if="error">Erreur: {{ error }}</p>
      <p v-else>Réunion non trouvée.</p>
      <!-- Le bouton de retour dans ce bloc utilise un style spécifique -->
      <div class="back-button-container">
        <BaseButton variant="secondary" @click="$router.push('/')">
          <FeatherIcon type="arrow-left" size="16"></FeatherIcon> Retour à la
          liste
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/components/BaseButton.vue";
import FeatherIcon from "vue-feather";
import apiService from "@/services/apiService"; // Assurez-vous que le chemin est correct

export default {
  name: "MeetingDetailView",
  components: {
    BaseButton,
    FeatherIcon,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      meeting: null,
      loading: false, // pour le chargement de la réunion principale
      error: null, // pour les erreurs de chargement de la réunion principale

      // Pour le formulaire d'ajout d'action
      newAction: {
        description: "",
        due_date: null,
        assigned_to: null,
        meeting_id: this.id,
      },
      actionLoading: false, // pour l'état de chargement de l'ajout/update/delete d'action
      actionError: null, // pour les erreurs liées aux actions

      // Pour l'édition inline d'action
      editingActionId: null, // ID de l'action en cours d'édition, null si aucune
      currentEditingAction: null, // Copie des données de l'action en cours d'édition
    };
  },
  computed: {
    formattedDate() {
      if (!this.meeting || !this.meeting.meeting_date) return "";
      const [year, month, day] = this.meeting.meeting_date
        .split("-")
        .map(Number);
      const dateObj = new Date(year, month - 1, day); // Month is 0-indexed
      return dateObj.toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    formattedTime() {
      if (!this.meeting || !this.meeting.meeting_time) return "";
      const [hours, minutes] = this.meeting.meeting_time.split(":").map(Number);
      const timeObj = new Date(2000, 0, 1, hours, minutes);
      return timeObj.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  created() {
    this.fetchMeeting();
  },
  methods: {
    async fetchMeeting() {
      this.loading = true;
      this.error = null;
      try {
        const data = await apiService.getMeeting(this.id);
        this.meeting = data;
        // Après chargement, si on était en train d'éditer (ex: refresh page), annuler l'édition
        this.cancelEditAction();
      } catch (e) {
        this.error = "Impossible de charger la réunion: " + e.message;
        console.error("Fetching meeting failed:", e);
      } finally {
        this.loading = false;
      }
    },
    // Méthode pour formater la date d'échéance d'une action (plus courte)
    formattedActionDate(dateString) {
      if (!dateString) return "";
      const [year, month, day] = dateString.split("-").map(Number);
      const dateObj = new Date(year, month - 1, day);
      return dateObj.toLocaleDateString("fr-FR", {
        month: "short",
        day: "numeric",
      }); // Ex: "oct. 26"
    },
    // Détermine l'icône Feather en fonction du statut de l'action
    getActionStatusIcon(status) {
      switch (status) {
        case "done":
          return "check-circle";
        case "in_progress":
          return "loader"; // 'refresh-cw' ou 'settings' sont d'autres options
        case "open":
          return "circle"; // 'square' est une autre option
        default:
          return "circle";
      }
    },
    // Bascule le statut de l'action et appelle l'API
    async toggleActionStatus(action) {
      // Empêche le clic pendant une autre opération (chargement, autre édition)
      if (this.actionLoading || this.editingActionId !== null || this.loading)
        return;

      const nextStatus =
        action.status === "open"
          ? "in_progress"
          : action.status === "in_progress"
          ? "done"
          : "open";

      const originalStatus = action.status;
      // Mise à jour optimiste de l'UI - MAJ directe de l'objet dans le tableau
      const index = this.meeting.actions.findIndex((a) => a.id === action.id);
      if (index !== -1) {
        const actionToUpdate = {
          ...this.meeting.actions[index],
          status: nextStatus,
        };
        this.meeting.actions.splice(index, 1, actionToUpdate); // Remplace l'objet original par le nouveau
      }

      this.actionLoading = true; // Active le chargement pour cette opération spécifique
      this.actionError = null;

      try {
        await apiService.updateAction(action.id, { status: nextStatus });
      } catch (e) {
        console.error("Failed to update action status:", e);
        // Annuler la mise à jour UI en cas d'erreur
        if (index !== -1) {
          const actionToRevert = {
            ...this.meeting.actions[index],
            status: originalStatus,
          };
          this.meeting.actions.splice(index, 1, actionToRevert);
        }
        this.actionError =
          "Erreur lors de la mise à jour du statut de l'action: " + e.message;
        alert(this.actionError); // Afficher un message d'erreur
      } finally {
        this.actionLoading = false; // Désactive le chargement
      }
    },
    // Crée une nouvelle action via l'API et l'ajoute à la liste locale
    async createAction() {
      if (!this.newAction.description) {
        this.actionError = "La description de l'action est requise.";
        return;
      }
      // Empêcher la double soumission ou la soumission pendant édition/autre action
      if (this.actionLoading || this.editingActionId !== null || this.loading)
        return;

      this.actionLoading = true; // Active le chargement
      this.actionError = null;

      try {
        const actionDataToSend = { ...this.newAction };
        // Nettoyer les champs vides ou non définis pour qu'ils soient envoyés comme null
        if (
          actionDataToSend.due_date === "" ||
          actionDataToSend.due_date === undefined
        )
          actionDataToSend.due_date = null;
        if (
          actionDataToSend.assigned_to === "" ||
          actionDataToSend.assigned_to === undefined
        )
          actionDataToSend.assigned_to = null;

        const createdAction = await apiService.createAction(actionDataToSend);
        console.log("Action created:", createdAction);

        // Ajouter la nouvelle action à la liste locale de manière réactive
        if (!this.meeting.actions) {
          this.meeting.actions = [];
        }
        // Utiliser splice pour s'assurer que Vue détecte le changement dans le tableau
        this.meeting.actions.splice(0, 0, createdAction); // Ajoute au début de la liste

        // Réinitialiser le formulaire pour la prochaine action
        this.newAction.description = "";
        this.newAction.due_date = null; // Réinitialiser avec null
        this.newAction.assigned_to = null; // Réinitialiser avec null
      } catch (e) {
        this.actionError =
          "Erreur lors de la création de l'action: " + e.message;
        console.error("Create action failed:", e);
        alert(this.actionError); // Afficher un message d'erreur
      } finally {
        this.actionLoading = false; // Désactive le chargement
      }
    },

    // Démarre l'édition inline d'une action
    startEditAction(action) {
      // Empêcher si une autre opération est en cours ou si déjà en édition ou chargement principal
      if (this.actionLoading || this.editingActionId !== null || this.loading)
        return;

      this.cancelEditAction(); // Annule l'édition précédente si active
      this.editingActionId = action.id;
      // Créer une copie profonde des données de l'action pour l'édition (pour pouvoir annuler)
      this.currentEditingAction = JSON.parse(JSON.stringify(action));
      // CORRECTION ICI : Réinitialiser actionLoading et actionError au début de l'édition
      this.actionLoading = false; // Assurez-vous que le loader n'est pas actif en entrant en mode édition
      this.actionError = null; // Nettoyer l'erreur précédente

      // Assurez-vous que due_date et assigned_to sont des chaînes vides si NULL, pour les inputs type date/text
      if (this.currentEditingAction.due_date === null) {
        this.currentEditingAction.due_date = "";
      }
      if (this.currentEditingAction.assigned_to === null) {
        this.currentEditingAction.assigned_to = "";
      }
      // TODO: Focus sur le premier champ du formulaire inline après l'activation (nécessite nextTick)
      // this.$nextTick(() => { this.$refs[`editDescriptionInput-${action.id}`].focus(); });
    },
    // Annule l'édition inline d'une action
    cancelEditAction() {
      this.editingActionId = null;
      this.currentEditingAction = null;
      this.actionError = null; // Nettoyer l'erreur si elle était affichée pour l'édition
      // CORRECTION ICI : Réinitialiser actionLoading lors de l'annulation
      this.actionLoading = false;
    },
    // Enregistre les modifications d'une action
    async saveEditAction() {
      if (
        !this.currentEditingAction ||
        !this.currentEditingAction.description
      ) {
        this.actionError = "La description de l'action est requise.";
        return;
      }
      // Empêcher double soumission ou soumission pendant chargement principal
      if (this.actionLoading || this.loading) return;

      this.actionLoading = true; // Active le chargement pour l'enregistrement
      this.actionError = null;

      try {
        // Créer un objet avec seulement les champs modifiés à envoyer à l'API
        // On envoie description, due_date, assigned_to, et statut.
        const updateData = {
          description: this.currentEditingAction.description,
          due_date:
            this.currentEditingAction.due_date === ""
              ? null
              : this.currentEditingAction.due_date,
          assigned_to:
            this.currentEditingAction.assigned_to === ""
              ? null
              : this.currentEditingAction.assigned_to,
          // IMPORTANT: Inclure le statut actuel pour qu'il ne soit pas perdu si l'API PUT ne le garde pas par défaut
          status: this.currentEditingAction.status, // Assurez-vous que le statut est envoyé
        };

        // Appeler le service pour mettre à jour l'action
        const updatedAction = await apiService.updateAction(
          this.editingActionId,
          updateData
        );
        console.log("Action updated:", updatedAction);

        // Trouver l'action dans la liste locale et mettre à jour ses données
        const index = this.meeting.actions.findIndex(
          (a) => a.id === this.editingActionId
        );
        if (index !== -1) {
          // Remplacer l'objet original dans le tableau par l'objet retourné par l'API
          // Utiliser splice pour assurer la réactivité
          this.meeting.actions.splice(index, 1, updatedAction);
        }

        this.cancelEditAction(); // Sortir du mode édition
      } catch (e) {
        this.actionError =
          "Erreur lors de l'enregistrement de l'action: " + e.message;
        console.error("Save action failed:", e);
        alert(this.actionError); // Afficher un message d'erreur
      } finally {
        this.actionLoading = false; // Désactive le chargement
      }
    },
    // Supprime une action
    async deleteAction(actionId) {
      if (!confirm("Êtes-vous sûr de vouloir supprimer cette action ?")) {
        return;
      }
      // Empêcher la double suppression ou suppression pendant édition/autre action/chargement principal
      if (this.actionLoading || this.editingActionId !== null || this.loading)
        return;

      this.actionLoading = true; // Active le chargement pour la suppression
      this.actionError = null;

      try {
        await apiService.deleteAction(actionId);
        console.log(`Action ${actionId} supprimée.`);
        // Supprimer l'action de la liste locale sans recharger la réunion complète
        // Utiliser filter pour créer une nouvelle référence tableau et assurer la réactivité
        this.meeting.actions = this.meeting.actions.filter(
          (action) => action.id !== actionId
        );
        // Si l'action supprimée était celle en cours d'édition
        if (this.editingActionId === actionId) {
          this.cancelEditAction();
        }
      } catch (e) {
        this.actionError =
          `Erreur lors de la suppression de l'action ${actionId}: ` + e.message;
        console.error(`Delete action ${actionId} failed:`, e);
        alert(this.actionError); // Afficher un message d'erreur
      } finally {
        this.actionLoading = false; // Désactive le chargement
      }
    },

    // Navigation vers la page d'édition de la réunion
    editMeeting(id) {
      // Empêcher si une autre opération est en cours
      if (this.loading || this.actionLoading || this.editingActionId !== null)
        return;
      this.$router.push({ name: "EditMeeting", params: { id: id } });
    },
    // Supprimer la réunion
    async deleteMeeting(id) {
      if (
        !confirm(
          "Êtes-vous sûr de vouloir supprimer cette réunion ? Cette action est irréversible et supprimera aussi toutes les actions liées."
        )
      ) {
        return;
      }

      // Empêcher si une autre opération est en cours
      if (this.loading || this.actionLoading || this.editingActionId !== null)
        return;

      this.loading = true; // Utilise loading de la réunion pour désactiver la page
      this.error = null; // Utilise l'erreur de la réunion pour la suppression de la réunion

      try {
        await apiService.deleteMeeting(id);
        console.log(`Réunion ${id} supprimée.`);
        // Rediriger vers la liste des réunions après suppression
        this.$router.push({ name: "MeetingList" });
      } catch (e) {
        this.error =
          `Erreur lors de la suppression de la réunion ${id}: ` + e.message;
        console.error(`Delete meeting ${id} failed:`, e);
        alert(this.error); // Afficher un message d'erreur
      } finally {
        this.loading = false; // Réactiver les boutons (si redirection échoue)
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/css/_variables.scss";

.meeting-detail-page {
  max-width: 900px;
  margin: $space-xl auto;
  padding: 0 $space-md;
}

// Copie des styles de base des blocs pour éviter les problèmes de @extend avec scoped
.app-block {
  background-color: $color-surface;
  padding: $space-lg;
  margin-bottom: $space-lg;
  border-radius: $border-radius-lg;
  box-shadow: $shadow-md;
}

.app-block-title {
  font-family: $font-heading;
  font-size: $font-size-xl;
  color: $color-text-900;
  margin-bottom: $space-lg;
  padding-bottom: $space-md; // Ajusté de $space-sm à $space-md pour plus d'espace
  border-bottom: 1px solid $color-primary-100;
  display: flex; // Permet l'alignement horizontal
  align-items: center;
  flex-wrap: wrap; // Permet le retour à la ligne si l'espace manque

  .feather-icon {
    flex-shrink: 0; // L'icône ne rétrécit pas
    margin-right: $space-sm; // Espace entre l'icône et le titre
    color: $color-primary-500;
  }

  h1,
  h2 {
    flex-grow: 1; // Le titre prend l'espace disponible
    margin: 0; // Réinitialiser les marges par défaut
    padding: 0;
    line-height: 1.2;
  }

  // Styles pour le conteneur des boutons d'action de la réunion
  .meeting-actions {
    flex-shrink: 0; // Les boutons ne rétrécissent pas
    display: flex;
    align-items: center;
    gap: $space-sm; // Espace entre les boutons
    margin-left: $space-md; // Espace entre le titre et les boutons (si pas wrapped)

    @media (max-width: $breakpoint-md) {
      // Exemple de responsive basique - ajusté à md
      margin-top: $space-sm; // Marge en haut si wrapped sur petite vue
      margin-left: 0; // Pas de marge gauche
      width: 100%; // Occuper toute la largeur
      justify-content: flex-end; // Aligner à droite en bas
    }
  }
}

// Style spécifique quand la réunion n'est PAS trouvée ou en chargement/erreur
.message-block {
  text-align: center;
  p {
    margin-bottom: $space-md;
    font-size: $font-size-lg;
  }
  .back-button-container {
    margin-top: $space-md;
  }
}

.meeting-meta {
  font-size: $font-size-sm;
  color: $color-text-600;
  margin-top: $space-sm; // Ajusté pour ne pas être négatif
  padding-top: $space-sm; // Petite marge au-dessus
  border-top: 1px solid $color-background; // Ligne fine pour séparer de l'en-tête/titre
  p {
    margin-bottom: $space-xs;
    display: flex;
    align-items: center;
    .feather-icon {
      margin-right: $space-xs;
      color: $color-text-300;
    }
  }
}

.notes-block {
  .notes-content {
    line-height: 1.8;
    color: $color-text-600;
  }
}

.actions-block {
  .add-action-form {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    gap: $space-md;
    align-items: center;
    margin-bottom: $space-lg;
    padding-bottom: $space-md;
    border-bottom: 1px solid $color-primary-100;

    input[type="text"],
    input[type="date"] {
      padding: $space-sm;
      border: 1px solid $color-primary-100;
      border-radius: $border-radius-sm;
      font-size: $font-size-base;
      font-family: $font-body;
      color: $color-text-600;
      background-color: $color-background;
      &:focus {
        outline: none;
        border-color: $color-primary-500;
        box-shadow: 0 0 0 3px rgba($color-primary-500, 0.2);
      }
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background-color: lighten($color-background, 2%);
      }
    }
    input[type="text"]::placeholder,
    input[type="date"]::placeholder {
      color: $color-text-300;
    }

    input[type="text"]:first-child {
      min-width: 150px;
    }

    .base-button {
      padding: $space-sm $space-md;
    }
  }

  .actions-list {
    // Styles pour TransitionGroup
    .list-enter-active,
    .list-leave-active {
      transition: all $transition-duration-medium ease;
    }
    .list-enter-from,
    .list-leave-to {
      opacity: 0;
      transform: translateX(30px);
    }
    .list-leave-active {
      position: absolute; /* Permet aux éléments suivants de glisser */
    }

    .action-item {
      /* Styles par défaut pour l'item, actifs en mode normal ET édition */
      display: flex; // Défini par défaut (mode normal) et écrasé en mode édition (grid)
      align-items: center;
      padding: $space-sm 0; // Padding en mode normal
      border-bottom: 1px solid $color-background;
      font-size: $font-size-base;
      color: $color-text-600;
      transition: background-color $transition-duration-short ease-in-out,
        padding $transition-duration-short ease-in-out,
        border-radius $transition-duration-short ease-in-out;

      &:last-child {
        border-bottom: none;
      }

      // Styles spécifiques quand l'action est en cours d'édition
      &.is-editing {
        background-color: $color-primary-100;
        padding: $space-md $space-sm; // Plus de padding en mode édition
        border-radius: $border-radius-sm;
        display: grid; // Passage en mode grid
        grid-template-columns: auto 1fr auto auto auto auto;
        gap: $space-sm;
        align-items: center;
      }
      &.is-loading {
        opacity: 0.8;
        pointer-events: none;
      }

      // Icône de statut (toujours visible, style change avec le statut)
      .action-status-icon {
        flex-shrink: 0;
        cursor: pointer;

        .feather-icon {
          transition: color $transition-duration-short ease-in-out;
          display: block;

          &.status-open {
            color: $color-text-300;
          }
          &.status-in-progress {
            color: $color-warning;
            animation: pulse 2s infinite;
          }
          &.status-done {
            color: $color-success;
          }
        }
        @keyframes pulse {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
          100% {
            opacity: 1;
          }
        }
      }

      .action-status-icon > i {
        vertical-align: middle !important;
      }

      /* -- CONTRÔLE DE LA VISIBILITÉ DES ÉLÉMENTS EN MODE NORMAL vs ÉDITION -- */

      // Éléments visibles SEULEMENT en mode NORMAL: Description, Meta, Icones Modifier/Supprimer
      // On les cache quand le parent .action-item a la classe .is-editing

      .action-description,
      .action-meta,
      .action-item-actions .action-icon {
        .action-item.is-editing & {
          // Cache ces éléments quand le parent est en mode édition
          display: none !important; /* Ajout !important ici aussi par sécurité */
        }
      }

      // Éléments visibles SEULEMENT en mode ÉDITION: Inputs, Boutons OK/Annuler
      // On les cache par défaut et les affiche quand le parent .action-item a la classe .is-editing

      input.edit-input,
      .action-edit-form .button-submit-edit,
      .action-edit-form .button-cancel-edit {
        //display: none !important; // Caché par défaut AVEC !important

        .action-item.is-editing & {
          // Affiche ces éléments quand le parent est en mode édition
          display: initial !important; // Utilise initial pour revenir au comportement par défaut de l'élément (block, flex, etc.)
        }
      }
      /* -- FIN CONTRÔLE VISIBILITÉ -- */

      // Styles spécifiques pour la description (visble en mode normal)
      .action-description {
        flex-grow: 1;
        // Règle d'affichage en mode normal
        .action-item:not(.is-editing) & {
          display: block;
        }
        &.status-done {
          text-decoration: line-through;
          color: $color-text-300;
        }
      }

      // Styles spécifiques pour les métadonnées (Assigné, Date) (visble en mode normal)
      .action-meta {
        flex-shrink: 0;
        display: flex; /* Reste flex à l'intérieur */
        align-items: center;
        font-size: $font-size-sm;
        color: $color-text-300;
        white-space: nowrap;
        span {
          display: flex;
          align-items: center;
          margin-left: $space-md;
          .feather-icon {
            margin-right: $space-xs;
            color: $color-text-300;
          }
        }
        span:first-child {
          margin-left: 0;
        }
      }

      // Styles pour le formulaire d'édition inline
      .action-edit-form {
        display: contents; // Permet aux enfants de participer à la grille parent sans créer de nouveau conteneur flex/grid
        // Attention : display:contents peut avoir des effets secondaires sur l'accessibilité et le box model.
        // Si ça pose problème, on reviendra à un display: flex ou grid ici et on ajustera la grille parente.
      }

      // Styles spécifiques pour les champs d'édition inline (visibles en mode édition)
      input.edit-input {
        width: 100%; /* Occupe la largeur de sa colonne dans la grille */
        padding: $space-xs $space-sm;
        border: 1px solid $color-primary-100;
        border-radius: $border-radius-sm;
        font-size: $font-size-base;
        font-family: $font-body;
        color: $color-text-600;
        background-color: $color-background;
        &:focus {
          outline: none;
          border-color: $color-primary-500;
          box-shadow: 0 0 0 3px rgba($color-primary-500, 0.2);
        }
        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background-color: lighten($color-background, 2%);
        }
      }
      input.edit-input[type="date"],
      input.edit-input[type="text"]:not(:first-of-type) {
        min-width: 80px;
      }

      // Styles pour le conteneur des boutons d'action (modifier/supprimer ou ok/annuler)
      .action-item-actions {
        display: flex; /* Toujours flex */
        align-items: center;
        gap: $space-sm;
        flex-shrink: 0;

        // Icônes Modifier/Supprimer (visibles en mode normal)
        .action-icon {
          cursor: pointer;
          color: $color-text-300;
          transition: color $transition-duration-short ease-in-out,
            transform $transition-duration-short ease-in-out;
          display: block;
          &:hover {
            transform: scale(1.1);
            color: $color-primary-500;
          }
          &--edit:hover {
            color: $color-accent-500;
          }
          &--delete:hover {
            color: $color-error;
          }
        }

        // NOUVEAU : Styles pour les boutons submit et button (OK/Annuler) en mode édition
        button.button-submit-edit,
        button.button-cancel-edit {
          // Utiliser les styles d'un BaseButton simple
          display: inline-flex; // Affiché par défaut (sera géré par les règles de visibilité ci-dessus)
          align-items: center;
          justify-content: center;
          padding: $space-xs $space-sm;
          border: none;
          border-radius: $border-radius-sm;
          cursor: pointer;
          font-weight: bold;
          font-size: $font-size-sm; // Plus petit en mode édition inline
          transition: background-color $transition-duration-short ease-in-out,
            transform $transition-duration-short ease-in-out,
            box-shadow $transition-duration-short ease-in-out,
            opacity $transition-duration-short ease-in-out;
          outline: none;
          gap: $space-xs / 2; // Espace entre icône et texte si on en met

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none !important;
            box-shadow: none !important;
          }

          .feather-icon {
            // Pas de marge à droite ici, le gap gère l'espace
            margin-right: 0;
          }
        }
        // Styles spécifiques pour chaque bouton
        button.button-submit-edit {
          background-color: $color-primary-500;
          color: $color-surface;
          &:not(:disabled):hover {
            background-color: darken($color-primary-500, 10%);
            transform: translateY(-1px);
            box-shadow: $shadow-sm;
          }
          &:not(:disabled):active {
            transform: translateY(0);
            box-shadow: none;
          }
        }
        button.button-cancel-edit {
          background-color: $color-background;
          color: $color-text-900;
          border: 1px solid $color-primary-100;
          &:not(:disabled):hover {
            background-color: darken($color-background, 5%);
            border-color: $color-primary-500;
            transform: translateY(-1px);
            box-shadow: $shadow-sm;
          }
          &:not(:disabled):active {
            transform: translateY(0);
            box-shadow: none;
          }
        }
      }
    }
  }

  .no-actions-message {
    text-align: center;
    color: $color-text-300;
    padding: $space-lg;
  }

  .error-message {
    color: $color-error;
    text-align: center;
    margin-top: $space-md;
  }
}

.back-button-block {
  text-align: center;
  margin-top: $space-xl;
  background: none;
  box-shadow: none;
  padding: 0;
}
</style>
